import React, { FC, useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, TextField, Typography } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
	DateRange,
	StaticDateRangePicker,
	TimePicker,
	dateRangeCalendarClasses,
	pickersLayoutClasses,
	renderTimeViewClock
} from "@mui/x-date-pickers-pro";
import moment, { Moment } from "moment";

import "./dateRangeSelectionModal.css";
import { EVENT_LOGS_REQUEST_TIME_FORMAT } from "../../utils/constants";

export type SelectedRangeType = "today" | "yesterday" | "7 days" | "month" | "3 months" | "6 months" | "custom";

interface IDateRangeSelectionModalProps {
	defaultValue?: { start: string; end: string };
	defaultRangeType?: SelectedRangeType;
	onDateRangeChange: (startTime: string, endTime: string, filterType: SelectedRangeType) => void;
}

interface IDefinedFilterDetails {
	key: SelectedRangeType;
	name: string;
}

interface ICustomTimeRangeDetails {
	startTime: Moment;
	endTime: Moment;
}

const DEFINED_FILTERS_LIST: IDefinedFilterDetails[] = [
	{ key: "today", name: "Live / Today" },
	{ key: "yesterday", name: "Yesterday" },
	{ key: "7 days", name: "Last 7 Days" },
	{ key: "month", name: "Last Month" },
	{ key: "3 months", name: "Last 3 Months" },
	{ key: "6 months", name: "Last 6 Months" }
];

const DateRangeSelectionModal: FC<IDateRangeSelectionModalProps> = (props) => {
	const { defaultValue, defaultRangeType, onDateRangeChange } = props;

	const [selectedRangeType, setSelectedRangeType] = useState<SelectedRangeType>("today");
	const [showDateRangeSelectionDialog, setShowDateRangeSelectionDialog] = useState<boolean>(false);
	const [customRange, setCustomRange] = useState<ICustomTimeRangeDetails>({
		startTime: moment().startOf("day"),
		endTime: moment().endOf("day")
	});

	const buttonLabel = useMemo(() => {
		if (defaultRangeType !== "custom") {
			const selectedRange = DEFINED_FILTERS_LIST.find((item) => item.key === defaultRangeType);
			if (selectedRange) return selectedRange.name;
		}

		return `${customRange.startTime.format("DD/MM/YYYY")} - ${customRange.endTime.format("DD/MM/YYYY")}`;
	}, [customRange, defaultRangeType]);

	function handleOpenDateRangeSelectionDialog() {
		setShowDateRangeSelectionDialog(true);
	}

	function handleCloseDateRangeSelectionDialog() {
		setShowDateRangeSelectionDialog(false);
	}

	function handleApplyDateFilters() {
		const startTimeString = customRange.startTime.format(EVENT_LOGS_REQUEST_TIME_FORMAT);
		const endTimeString = customRange.endTime.format(EVENT_LOGS_REQUEST_TIME_FORMAT);

		onDateRangeChange(startTimeString, endTimeString, selectedRangeType);
		handleCloseDateRangeSelectionDialog();
	}

	function handleChangeDateRangeValues(value: DateRange<moment.Moment>) {
		setSelectedRangeType("custom");
		setCustomRange((prev) => {
			const updatedValues = { ...prev };

			if (value[0]) {
				updatedValues.startTime = updatedValues.startTime
					.clone()
					.set("date", value[0].get("date"))
					.set("month", value[0].get("month"))
					.set("year", value[0].get("year"));
			}

			if (value[1]) {
				updatedValues.endTime = updatedValues.endTime
					.clone()
					.set("date", value[1].get("date"))
					.set("month", value[1].get("month"))
					.set("year", value[1].get("year"));
			}

			return updatedValues;
		});
	}

	function handleChangeTimeRangeValues(value: moment.Moment, key: "startTime" | "endTime") {
		setSelectedRangeType("custom");
		setCustomRange((prev) => ({
			...prev,
			[key]: prev[key].clone().set("hours", value.get("hours")).set("minutes", value.get("minutes")).set("seconds", 0)
		}));
	}

	function handleChangeSelectedRange(updatedValue: SelectedRangeType) {
		switch (updatedValue) {
			case "today":
				setCustomRange({
					startTime: moment().startOf("day"),
					endTime: moment().endOf("day")
				});
				break;

			case "yesterday":
				setCustomRange({
					startTime: moment().subtract(1, "day").startOf("day"),
					endTime: moment().subtract(1, "day").endOf("day")
				});
				break;

			case "7 days":
				setCustomRange({
					startTime: moment().subtract(7, "days").startOf("day"),
					endTime: moment().endOf("day")
				});
				break;

			case "month":
				setCustomRange({
					startTime: moment().subtract(1, "month").startOf("day"),
					endTime: moment().endOf("day")
				});
				break;

			case "3 months":
				setCustomRange({
					startTime: moment().subtract(3, "months").startOf("day"),
					endTime: moment().endOf("day")
				});
				break;

			case "6 months":
				setCustomRange({
					startTime: moment().subtract(6, "months").startOf("day"),
					endTime: moment().endOf("day")
				});
				break;

			default:
				break;
		}

		setSelectedRangeType(updatedValue);
	}

	useEffect(() => {
		if (defaultValue) {
			setCustomRange({
				startTime: moment(defaultValue.start, EVENT_LOGS_REQUEST_TIME_FORMAT),
				endTime: moment(defaultValue.end, EVENT_LOGS_REQUEST_TIME_FORMAT)
			});
		}
	}, [defaultValue]);

	useEffect(() => {
		if (defaultRangeType) {
			setSelectedRangeType(defaultRangeType);
		}
	}, [defaultRangeType]);

	const startDateString = customRange.startTime.format("DD/MM/YYYY");
	const endDateString = customRange.endTime.format("DD/MM/YYYY");

	return (
		<>
			<Box className="dashboard-time-range-selector" onClick={handleOpenDateRangeSelectionDialog}>
				<CalendarTodayIcon />

				<Typography fontWeight={500}>{buttonLabel}</Typography>
			</Box>

			<Dialog
				open={showDateRangeSelectionDialog}
				onClose={handleCloseDateRangeSelectionDialog}
				maxWidth="xs"
				fullWidth
				sx={{
					"& .MuiDialog-paper": {
						margin: { xs: "16px", sm: "24px" },
						maxHeight: { xs: "calc(100vh - 32px)", sm: "calc(100vh - 48px)" },
						width: { xs: "calc(100vw - 32px)", sm: "calc(100vw - 48px)" }
					}
				}}
			>
				<DialogContent
					classes={{ root: "date-range-selection-modal-content-wrapper" }}
					sx={{
						padding: { xs: "16px !important", sm: "20px !important" }
					}}
				>
					<Box>
						<Box
							className="predefined-date-value-selection-wrapper"
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
								gap: { xs: "8px", sm: "12px" },
								marginBottom: { xs: "16px", sm: "20px" }
							}}
						>
							{DEFINED_FILTERS_LIST.map((definedFilterItem) => (
								<Box
									className={`predefined-date-value ${selectedRangeType === definedFilterItem.key ? "active" : ""}`}
									onClick={() => handleChangeSelectedRange(definedFilterItem.key)}
									key={definedFilterItem.key}
									sx={{
										padding: { xs: "8px 6px", sm: "10px 8px" },
										"& .MuiTypography-root": {
											fontSize: { xs: "0.75rem", sm: "0.875rem" }
										}
									}}
								>
									<Typography>{definedFilterItem.name}</Typography>
								</Box>
							))}
						</Box>
					</Box>

					<Typography
						variant="h6"
						marginTop="1rem"
						sx={{
							fontSize: { xs: "1rem", sm: "1.125rem" },
							marginBottom: { xs: "12px", sm: "16px" }
						}}
					>
						Please select start and end date
					</Typography>

					<Box
						className="date-range-toolbar-section-wrapper"
						sx={{
							marginBottom: { xs: "16px", sm: "20px" }
						}}
					>
						<Box
							className="date-range-inputs-wrapper"
							sx={{
								display: "flex",
								flexDirection: { xs: "column", sm: "row" },
								gap: { xs: "12px", sm: "16px" }
							}}
						>
							<TextField
								label="From"
								value={startDateString}
								size="small"
								fullWidth
								autoComplete="off"
								sx={{
									"& .MuiInputBase-input": {
										fontSize: { xs: "0.875rem", sm: "1rem" }
									},
									"& .MuiInputLabel-root": {
										fontSize: { xs: "0.875rem", sm: "1rem" }
									}
								}}
							/>
							<TextField
								label="To"
								value={endDateString}
								size="small"
								fullWidth
								autoComplete="off"
								sx={{
									"& .MuiInputBase-input": {
										fontSize: { xs: "0.875rem", sm: "1rem" }
									},
									"& .MuiInputLabel-root": {
										fontSize: { xs: "0.875rem", sm: "1rem" }
									}
								}}
							/>
						</Box>
					</Box>

					<Typography
						variant="h6"
						sx={{
							fontSize: { xs: "1rem", sm: "1.125rem" },
							marginBottom: { xs: "12px", sm: "16px" }
						}}
					>
						Please select start and end time
					</Typography>

					<Box
						className="date-range-toolbar-section-wrapper"
						sx={{
							marginBottom: { xs: "16px", sm: "20px" }
						}}
					>
						<Box
							className="date-range-inputs-wrapper"
							sx={{
								display: "flex",
								flexDirection: { xs: "column", sm: "row" },
								gap: { xs: "12px", sm: "16px" }
							}}
						>
							<TimePicker
								label="From"
								value={customRange.startTime}
								onChange={(value) => handleChangeTimeRangeValues(value ?? moment().startOf("day"), "startTime")}
								closeOnSelect={false}
								viewRenderers={{
									hours: renderTimeViewClock,
									minutes: renderTimeViewClock
								}}
								slotProps={{
									textField: {
										variant: "outlined",
										size: "small",
										fullWidth: true,
										sx: {
											"& .MuiInputBase-input": {
												fontSize: { xs: "0.875rem", sm: "1rem" }
											},
											"& .MuiInputLabel-root": {
												fontSize: { xs: "0.875rem", sm: "1rem" }
											}
										}
									}
								}}
							/>

							<TimePicker
								label="To"
								value={customRange.endTime}
								onChange={(value) => handleChangeTimeRangeValues(value ?? moment().endOf("day"), "endTime")}
								closeOnSelect={false}
								viewRenderers={{
									hours: renderTimeViewClock,
									minutes: renderTimeViewClock
								}}
								slotProps={{
									textField: {
										variant: "outlined",
										size: "small",
										fullWidth: true,
										sx: {
											"& .MuiInputBase-input": {
												fontSize: { xs: "0.875rem", sm: "1rem" }
											},
											"& .MuiInputLabel-root": {
												fontSize: { xs: "0.875rem", sm: "1rem" }
											}
										}
									}
								}}
							/>
						</Box>
					</Box>

					<StaticDateRangePicker
						disableFuture
						disableHighlightToday
						disableAutoMonthSwitching
						calendars={1}
						value={[customRange.startTime, customRange.endTime]}
						onChange={handleChangeDateRangeValues}
						sx={{
							[`.${pickersLayoutClasses.contentWrapper}`]: {
								alignItems: "center"
							},
							[`.${dateRangeCalendarClasses.monthContainer}`]: {
								border: "1px solid #00000099",
								borderRadius: "4px"
							},
							"& .MuiPickersCalendarHeader-root": {
								padding: { xs: "8px", sm: "12px" }
							},
							"& .MuiPickersCalendarHeader-label": {
								fontSize: { xs: "0.875rem", sm: "1rem" }
							},
							"& .MuiPickersDay-root": {
								fontSize: { xs: "0.75rem", sm: "0.875rem" },
								width: { xs: "28px", sm: "36px" },
								height: { xs: "28px", sm: "36px" }
							},
							"& .MuiDayCalendar-weekDayLabel": {
								fontSize: { xs: "0.75rem", sm: "0.875rem" }
							}
						}}
						slots={{
							toolbar: () => <></>,
							actionBar: () => <></>
						}}
					/>
				</DialogContent>

				<DialogActions
					sx={{
						justifyContent: "center",
						paddingBottom: { xs: 2, sm: 3 },
						paddingX: { xs: 2, sm: 3 }
					}}
				>
					<Button
						variant="contained"
						color="primary"
						onClick={handleApplyDateFilters}
						sx={{
							paddingX: { xs: 2, sm: 3 },
							paddingY: { xs: "8px", sm: "10px" },
							fontSize: { xs: "0.875rem", sm: "1rem" },
							minWidth: { xs: "80px", sm: "100px" }
						}}
					>
						Apply
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default DateRangeSelectionModal;
